class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.scrollY = 0;
    this.chatLauncherObserver = null;
    this.upsellHeadingObserver = null;
    this.suppressUpsellClickUntil = 0;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.bindUpsellVerticalScroll();
    this.bindUpsellSwipeNavigation();
  }

  bindUpsellVerticalScroll() {
    let lastTouchY = null;
    let lastTouchX = null;

    this.addEventListener(
      'wheel',
      (event) => {
        if (!event.target.closest('.cart-drawer__upsell-shell')) return;
        if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

        const scroller = event.target.closest('cart-drawer-items');
        if (!scroller) return;

        scroller.scrollTop += event.deltaY * 1.65;
        event.preventDefault();
        event.stopPropagation();
      },
      { passive: false, capture: true }
    );

    this.addEventListener(
      'touchstart',
      (event) => {
        if (!event.target.closest('.cart-drawer__upsell-shell') || !event.touches.length) return;

        lastTouchY = event.touches[0].clientY;
        lastTouchX = event.touches[0].clientX;
      },
      { passive: true, capture: true }
    );

    this.addEventListener(
      'touchmove',
      (event) => {
        if (!event.target.closest('.cart-drawer__upsell-shell') || !event.touches.length) return;
        if (lastTouchY === null || lastTouchX === null) return;

        const currentY = event.touches[0].clientY;
        const currentX = event.touches[0].clientX;
        const deltaY = lastTouchY - currentY;
        const deltaX = lastTouchX - currentX;

        if (Math.abs(deltaY) <= Math.abs(deltaX)) return;

        const scroller = event.target.closest('cart-drawer-items');
        if (!scroller) return;

        scroller.scrollTop += deltaY * 1.35;
        lastTouchY = currentY;
        lastTouchX = currentX;
        event.preventDefault();
        event.stopPropagation();
      },
      { passive: false, capture: true }
    );

    this.addEventListener(
      'touchend',
      () => {
        lastTouchY = null;
        lastTouchX = null;
      },
      { passive: true, capture: true }
    );
  }

  cleanEssentialUpsellHeadingArrow() {
    const upsellBlock = this.querySelector('.essential-upsell-cross-sell-block');
    if (!upsellBlock) return;

    const walker = document.createTreeWalker(upsellBlock, NodeFilter.SHOW_TEXT);
    let textNode = walker.nextNode();

    while (textNode) {
      const text = textNode.textContent || '';

      if (text.includes('↓') && /save|deal/i.test(text)) {
        textNode.textContent = text.replace(/\s*↓/g, '');
      }

      textNode = walker.nextNode();
    }
  }

  isEssentialUpsellInteractiveElement(element) {
    return Boolean(
      element.closest(
        'button, input, select, textarea, label, summary, [role="button"], [contenteditable="true"]'
      )
    );
  }

  getEssentialUpsellCarouselButton(direction) {
    const upsellBlock = this.querySelector('.essential-upsell-cross-sell-block');
    if (!upsellBlock) return null;

    const directionMatchers =
      direction === 'next' ? ['next', 'right', 'forward'] : ['previous', 'prev', 'left', 'back'];

    const controls = [
      ...upsellBlock.querySelectorAll('button, [role="button"], a[href], [aria-label], [title]'),
    ];

    return (
      controls.find((control) => {
        if (control.disabled || control.getAttribute('aria-disabled') === 'true') return false;
        if (control.offsetParent === null && getComputedStyle(control).position !== 'fixed') return false;

        const label = [
          control.getAttribute('aria-label'),
          control.getAttribute('title'),
          control.getAttribute('data-testid'),
          control.className,
          control.textContent,
        ]
          .join(' ')
          .toLowerCase();

        return directionMatchers.some((matcher) => label.includes(matcher));
      }) || null
    );
  }

  triggerEssentialUpsellSlide(direction) {
    const control = this.getEssentialUpsellCarouselButton(direction);

    if (!control) return false;

    control.click();
    return true;
  }

  bindUpsellSwipeNavigation() {
    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let pointerId = null;
    let pointerElement = null;
    let trackingSwipe = false;
    let horizontalSwipe = false;

    const resetSwipe = () => {
      pointerId = null;
      pointerElement = null;
      trackingSwipe = false;
      horizontalSwipe = false;
      startX = 0;
      startY = 0;
      startTime = 0;
    };

    this.addEventListener(
      'pointerdown',
      (event) => {
        if (!event.target.closest('.cart-drawer__upsell-shell')) return;
        if (event.button && event.button !== 0) return;
        if (this.isEssentialUpsellInteractiveElement(event.target)) return;

        pointerId = event.pointerId;
        pointerElement = event.target;
        trackingSwipe = true;
        horizontalSwipe = false;
        startX = event.clientX;
        startY = event.clientY;
        startTime = Date.now();

        if (pointerElement.setPointerCapture) {
          try {
            pointerElement.setPointerCapture(pointerId);
          } catch (error) {
            // Some app-rendered elements do not allow pointer capture.
          }
        }
      },
      { passive: true, capture: true }
    );

    this.addEventListener(
      'pointermove',
      (event) => {
        if (!trackingSwipe || event.pointerId !== pointerId) return;

        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;

        if (!horizontalSwipe && Math.abs(deltaX) > 14 && Math.abs(deltaX) > Math.abs(deltaY) * 1.15) {
          horizontalSwipe = true;
        }

        if (!horizontalSwipe) return;

        event.preventDefault();
        event.stopPropagation();
      },
      { passive: false, capture: true }
    );

    this.addEventListener(
      'pointerup',
      (event) => {
        if (!trackingSwipe || event.pointerId !== pointerId) return;

        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;
        const elapsed = Date.now() - startTime;
        resetSwipe();

        if (elapsed > 900) return;
        if (Math.abs(deltaX) < 34) return;
        if (Math.abs(deltaX) < Math.abs(deltaY) * 1.12) return;

        const direction = deltaX < 0 ? 'next' : 'previous';

        if (this.triggerEssentialUpsellSlide(direction)) {
          this.suppressUpsellClickUntil = Date.now() + 350;
          event.preventDefault();
          event.stopPropagation();
        }
      },
      { passive: false, capture: true }
    );

    this.addEventListener('pointercancel', resetSwipe, { passive: true, capture: true });

    this.addEventListener(
      'click',
      (event) => {
        if (Date.now() > this.suppressUpsellClickUntil) return;
        if (!event.target.closest('.cart-drawer__upsell-shell')) return;

        event.preventDefault();
        event.stopPropagation();
      },
      { capture: true }
    );
  }

  watchEssentialUpsellHeading() {
    if (this.upsellHeadingObserver) return;

    const upsellBlock = this.querySelector('.essential-upsell-cross-sell-block');
    if (!upsellBlock) return;

    this.upsellHeadingObserver = new MutationObserver(() => this.cleanEssentialUpsellHeadingArrow());
    this.upsellHeadingObserver.observe(upsellBlock, { childList: true, subtree: true, characterData: true });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  lockScroll() {
    this.scrollY = window.scrollY || window.pageYOffset;

    document.documentElement.classList.add('cart-drawer-open');
    document.body.classList.add('cart-drawer-open');

    document.body.style.top = `-${this.scrollY}px`;
  }

  unlockScroll() {
    document.documentElement.classList.remove('cart-drawer-open');
    document.body.classList.remove('cart-drawer-open');

    document.body.style.top = '';

    window.scrollTo(0, this.scrollY || 0);
  }

  getShopifyInboxLaunchers() {
    const selectors = [
      '#shopify-chat',
      '#ShopifyChat',
      'shopify-chat',
      'shopify-inbox',
      '[data-shopify-chat]',
      '[id*="shopify-chat" i]',
      '[id*="shopifyinbox" i]',
      '[id*="ShopifyChat"]',
      '[class*="shopify-chat" i]',
      '[class*="shopify-inbox" i]',
      'iframe[src*="shopify-chat" i]',
      'iframe[src*="shopifyinbox" i]',
      'iframe[src*="inbox" i]',
      'iframe[title*="chat" i]',
      'iframe[title*="inbox" i]',
    ];

    try {
      return [...document.querySelectorAll(selectors.join(','))].filter((element) => !element.closest('cart-drawer'));
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  hideShopifyInboxLaunchers() {
    this.getShopifyInboxLaunchers().forEach((launcher) => {
      if (launcher.dataset.cartDrawerChatHidden === 'true') return;

      launcher.dataset.cartDrawerChatHidden = 'true';
      launcher.dataset.cartDrawerChatDisplay = launcher.style.display || '';
      launcher.dataset.cartDrawerChatOpacity = launcher.style.opacity || '';
      launcher.dataset.cartDrawerChatVisibility = launcher.style.visibility || '';
      launcher.dataset.cartDrawerChatPointerEvents = launcher.style.pointerEvents || '';

      launcher.style.setProperty('display', 'none', 'important');
      launcher.style.setProperty('opacity', '0', 'important');
      launcher.style.setProperty('visibility', 'hidden', 'important');
      launcher.style.setProperty('pointer-events', 'none', 'important');
    });
  }

  restoreShopifyInboxLaunchers() {
    if (this.chatLauncherObserver) {
      this.chatLauncherObserver.disconnect();
      this.chatLauncherObserver = null;
    }

    document.querySelectorAll('[data-cart-drawer-chat-hidden="true"]').forEach((launcher) => {
      launcher.style.display = launcher.dataset.cartDrawerChatDisplay || '';
      launcher.style.opacity = launcher.dataset.cartDrawerChatOpacity || '';
      launcher.style.visibility = launcher.dataset.cartDrawerChatVisibility || '';
      launcher.style.pointerEvents = launcher.dataset.cartDrawerChatPointerEvents || '';

      delete launcher.dataset.cartDrawerChatHidden;
      delete launcher.dataset.cartDrawerChatDisplay;
      delete launcher.dataset.cartDrawerChatOpacity;
      delete launcher.dataset.cartDrawerChatVisibility;
      delete launcher.dataset.cartDrawerChatPointerEvents;
    });
  }

  watchShopifyInboxLaunchers() {
    if (this.chatLauncherObserver || !document.body) return;

    this.chatLauncherObserver = new MutationObserver(() => {
      if (this.classList.contains('active')) this.hideShopifyInboxLaunchers();
    });

    this.chatLauncherObserver.observe(document.body, { childList: true, subtree: true });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');

    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.lockScroll();
    this.hideShopifyInboxLaunchers();
    this.watchShopifyInboxLaunchers();
    this.cleanEssentialUpsellHeadingArrow();
    this.watchEssentialUpsellHeading();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);

    document.body.classList.remove('overflow-hidden');
    this.unlockScroll();
    this.restoreShopifyInboxLaunchers();

    if (this.upsellHeadingObserver) {
      this.upsellHeadingObserver.disconnect();
      this.upsellHeadingObserver = null;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (this.upsellHeadingObserver) {
      this.upsellHeadingObserver.disconnect();
      this.upsellHeadingObserver = null;
    }

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.cleanEssentialUpsellHeadingArrow();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
